import {useCallback, useEffect, useRef, useState} from 'react'

export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

export type PageFetcher<T> = (page: number, limit: number, search: string) => Promise<Paginated<T>>

interface Options {
  limit?: number
  // Extra dependencies (e.g. a status filter) that should reset to page 1 and refetch.
  deps?: unknown[]
}

// Server-side pagination + search hook. Debounces the search box (350ms), resets
// to page 1 on a new search/filter, and refetches whenever page/search/deps change.
export function usePaginated<T>(fetcher: PageFetcher<T>, opts: Options = {}) {
  const limit = opts.limit ?? 20
  const deps = opts.deps ?? []

  const [items, setItems]     = useState<T[]>([])
  const [total, setTotal]     = useState(0)
  const [page, setPage]       = useState(1)
  const [search, setSearch]   = useState('')
  const [debounced, setDebounced] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  // Keep the latest fetcher without making it a fetch dependency.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  // Debounce the search input.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350)
    return () => clearTimeout(t)
  }, [search])

  // A new search or filter starts from page 1.
  useEffect(() => {
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, ...deps])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    fetcherRef.current(page, limit, debounced)
      .then((res) => {
        if (!active) return
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((e: any) => {
        if (active) setError(e?.message ?? 'Failed to load')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debounced, reloadKey, ...deps])

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])
  const totalPages = Math.max(1, Math.ceil(total / limit))

  return {items, total, totalPages, page, setPage, search, setSearch, limit, loading, error, reload}
}
